import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
  TablePagination,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "../../utils/axiosInstance";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import InfoNotification from "../currentAlarm/InfoNotification";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDateUS, formatReadableDate } from "../../utils/formatDate";
//for pdf Export
export default function NotificationHistory(device) {
  const [expandedRows, setExpandedRows] = useState({});
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [notificationsQuery, setNotificationsQuery] = useState("");
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const [notificationFilter, setNotificationFilter] = useState("faultClear");

  // Pagination states for notifications
  const [notificationsPage, setNotificationsPage] = useState(0);
  const [notificationsRowsPerPage, setNotificationsRowsPerPage] = useState(10);

  const [notificationsExportMenuAnchor, setNotificationsExportMenuAnchor] =
    useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const [showInformationNotification, setShowInformationNotification] =
    useState(false);

  const OpenInfo = (notificationData) => {
    setSelectedNotification(notificationData);
    setShowInformationNotification(true);
  };

  const handleNotificationExportMenuClose = () => {
    setNotificationsExportMenuAnchor(null);
  };

  // Filter for Critical Alarm
  const handleCriticalAlarm = () => {
    setFilteredNotifications(alarms);
    setNotificationFilter("criticalAlarm");
    setNotificationsPage(0);
  };

  const handleFaultClear = () => {
    // If already filtered by Fault Clear, show all
    setFilteredNotifications(notifications);
    setNotificationFilter("faultClear");
    setNotificationsPage(0);
  };

  const handleNotificationsSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setNotificationsQuery(q);
    const filtered = notifications.filter((notif) => {
      return (
        (notif.notification || "").toLowerCase().includes(q) ||
        (notif.recipients || "").toLowerCase().includes(q) ||
        (notif.time || "").toLowerCase().includes(q)
      );
    });
    setFilteredNotifications(filtered);
    setNotificationsPage(0);
  };

  const handleNotificationExportMenuOpen = (event) => {
    setNotificationsExportMenuAnchor(event.currentTarget);
  };

  const paginatedNotifications = filteredNotifications.slice(
    notificationsPage * notificationsRowsPerPage,
    notificationsPage * notificationsRowsPerPage + notificationsRowsPerPage
  );

  const handleNotificationsChangePage = (event, newPage) => {
    setNotificationsPage(newPage);
  };
  const handleNotificationsChangeRowsPerPage = (event) => {
    setNotificationsRowsPerPage(parseInt(event.target.value, 10));
    setNotificationsPage(0);
  };

  const handleNotificationsExportMenuClose = () => {
    setNotificationsExportMenuAnchor(null);
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  // ===== NOTIFICATIONS EXPORT FUNCTIONS =====

  const copyNotificationsToClipboard = () => {
    const textContent = filteredNotifications
      .map((notif) => {
        return `${notif.time}\t${notif.type}\t${notif.notification}\t${
          notif.recipientsLength + notif.emailsLength
        }`;
      })
      .join("\n");

    const headers = "Date\tAlert\tAlert Text\tRecipients";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => console.log(`Notifications data copied to clipboard`))
      .catch((err) => console.error("Failed to copy:", err));

    handleNotificationsExportMenuClose();
    toast.success("Copied notification logs to clipboard");
  };

  const exportNotificationsToCSV = () => {
    const csvContent = [
      ["Date", "Alert", "Alert Text", "Recipients"],
      ...filteredNotifications.map((notif) => [
        notif.time,
        notif.type,
        notif.notification,
        notif.recipientsLength + notif.emailsLength,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Notifications_Log_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();

    handleNotificationsExportMenuClose();
    toast.success(`Exported Notifications to CSV`);
  };

  const exportNotificationsToExcel = () => {
    const headers = ["Date", "Alert", "Alert Text", "Recipients"];

    const rows = filteredNotifications.map((notif) => [
      notif.time,
      notif.type,
      notif.notification,
      notif.recipientsLength + notif.emailsLength,
    ]);

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Notifications");

    // Export file
    XLSX.writeFile(
      workbook,
      `Notifications_Log_${new Date().toISOString().split("T")[0]}.xlsx`
    );

    handleNotificationsExportMenuClose();
    toast.success(`Exported Notifications to Excel`);
  };

  const exportNotificationsToPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");

    doc.setFontSize(12);
    doc.text("Notifications Log Report", 40, 30);

    const headers = [["Date", "Alert", "Alert Text", "Recipients"]];

    const rows = filteredNotifications.map((notif) => [
      notif.time,
      notif.type,
      notif.notification,
      notif.recipientsLength + notif.emailsLength,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [211, 47, 47] }, // Red header color
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 120 }, // Date
        1: { cellWidth: 120 }, // Alert
        2: { cellWidth: 350 }, // Alert Text
        3: { cellWidth: 200 }, // Recipients
      },
    });

    doc.save(`Notifications_Log_${new Date().toISOString().split("T")[0]}.pdf`);
    handleNotificationsExportMenuClose();
    toast.success(`Exported Notifications to PDF`);
  };
  // ===== NOTIFICATIONS EXPORT FUNCTIONS =====

  // Fetch notifications data
  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      // Replace with your actual API call
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/alarms/allLogs/${device.device.id}`
      );
      //-------------- put this block instead if you want to keep only real notifications --------------//;
      //    let filtereddata = response.data.filter((notif) =>
      //   (notif.notification || "").includes("Fault Clear")
      // );
      //   setNotifications(filtereddata);
      //   setFilteredNotifications(filtereddata);
      //-------------- put this block instead if you want to keep only real notifications --------------//;
      setNotifications(response.data);
      setFilteredNotifications(response.data);
      setLoadingNotifications(false);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  {
    /* Notifications Log - Right Side */
  }
  return (
    <Grid item xs={12} md sx={{ height: "77vh", width: "100%" }}>
      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minWidth: "100%", // Add this
          position: "relative",
        }}
      >
        {/* Loading Overlay for Notifications */}
        {loadingNotifications && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(187, 183, 183, 0.12)",
              backdropFilter: "blur(10px)",
              zIndex: 10,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="#60a5fa" size={70} />
          </Box>
        )}

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Notifications Log
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                color="white"
                startIcon={<GetAppIcon />}
                onClick={handleNotificationExportMenuOpen}
              >
                Export
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Search Input */}
        <TextField
          label="Search:"
          variant="outlined"
          size="small"
          value={notificationsQuery}
          onChange={handleNotificationsSearch}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Notifications Table */}
        <TableContainer sx={{ flexGrow: 1, maxHeight: "calc(100vh - 300px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#000" }}>
                <TableCell
                  sx={{
                    display: { xs: "table-cell", md: "none" },
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "12px 8px",
                    width: "50px",
                    color: "white",
                    backgroundColor: "#000",
                  }}
                >
                  Details
                </TableCell>
                <TableCell
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    fontSize: {
                      xs: "0.7rem",
                      sm: "0.8rem",
                      md: "0.875rem",
                    },
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    fontSize: {
                      xs: "0.7rem",
                      sm: "0.8rem",
                      md: "0.875rem",
                    },
                  }}
                >
                  Alert
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    fontSize: {
                      xs: "0.7rem",
                      sm: "0.8rem",
                      md: "0.875rem",
                    },
                  }}
                >
                  Alert Text
                </TableCell>
                <TableCell
                  sx={{
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    fontSize: {
                      xs: "0.7rem",
                      sm: "0.8rem",
                      md: "0.875rem",
                    },
                  }}
                >
                  Recipients
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedNotifications.length > 0 ? (
                paginatedNotifications.map((notif, index) => (
                  <React.Fragment key={notif.id || index}>
                    <TableRow
                      key={notif.id}
                      hover
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        "&:hover": {
                          backgroundColor: "#4a4d54",
                        },
                      }}
                    >
                      {/* Expand Button - Mobile Only */}
                      <TableCell
                        sx={{
                          display: { xs: "table-cell", md: "none" },
                          textAlign: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(notif.id || index)}
                          sx={{ color: "white" }}
                        >
                          {expandedRows[notif.id || index] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.9rem",
                          },
                        }}
                      >
                        {formatDateUS(notif.time)}
                      </TableCell>
                      <TableCell
                        sx={{
                          color:
                            notif.type === "FAULT"
                              ? "red"
                              : notif.type === "CLEAR"
                              ? "green"
                              : "inherit",
                          fontWeight: "bold",
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.9rem",
                          },
                        }}
                      >
                        {notif.type}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.8rem",
                            md: "0.9rem",
                          },
                        }}
                      >
                        {notif.notification}
                      </TableCell>
                      <TableCell align="center"  sx={{
                          display: { xs: "none", md: "table-cell" },
                        }} > 
                        <Tooltip title="Open form">
                          <Box
                            onClick={
                              notif.recipientsLength + notif.emailsLength > 0
                                ? () => OpenInfo(notif)
                                : undefined
                            }
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor:
                                notif.recipientsLength + notif.emailsLength > 0
                                  ? "#bd0d0dff"
                                  : "grey.700",
                              borderRadius: "6px",
                              cursor: "pointer",
                              cursor:
                                notif.recipientsLength + notif.emailsLength > 0
                                  ? "pointer"
                                  : "not-allowed",
                              transition: "all 0.2s ease",
                              color: "white",
                              fontSize: 13,
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              "&:hover":
                                notif.recipientsLength + notif.emailsLength > 0
                                  ? {
                                      backgroundColor: "primary.main",
                                      transform: "scale(1.1)",
                                    }
                                  : {},
                            }}
                          >
                            {notif.recipientsLength + notif.emailsLength}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    {/* Mobile Collapsed Row */}
                    <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={4}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        }}
                      >
                        <Collapse
                          in={expandedRows[notif.id || index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Recipients:
                                </Typography>
                                <Tooltip title="Open form">
                                  <Box
                                    onClick={
                                      notif.recipientsLength +
                                        notif.emailsLength >
                                      0
                                        ? () => OpenInfo(notif)
                                        : undefined
                                    }
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      backgroundColor:
                                        notif.recipientsLength +
                                          notif.emailsLength >
                                        0
                                          ? "#bd0d0dff"
                                          : "grey.700",
                                      borderRadius: "6px",
                                      cursor: "pointer",
                                      cursor:
                                        notif.recipientsLength +
                                          notif.emailsLength >
                                        0
                                          ? "pointer"
                                          : "not-allowed",
                                      transition: "all 0.2s ease",
                                      color: "white",
                                      fontSize: 13,
                                      fontWeight: 600,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      "&:hover":
                                        notif.recipientsLength +
                                          notif.emailsLength >
                                        0
                                          ? {
                                              backgroundColor: "primary.main",
                                              transform: "scale(1.1)",
                                            }
                                          : {},
                                    }}
                                  >
                                    {notif.recipientsLength +
                                      notif.emailsLength}
                                  </Box>
                                </Tooltip>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Date:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {new Date(notif.time)
                                    .toISOString()
                                    .slice(0, 19)}{" "}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    No data available in table
                    <br />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredNotifications.length > 0 && (
          <TablePagination
            component="div"
            count={filteredNotifications.length}
            page={notificationsPage}
            onPageChange={handleNotificationsChangePage}
            rowsPerPage={notificationsRowsPerPage}
            onRowsPerPageChange={handleNotificationsChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{ mt: 1 }}
          />
        )}

        {/* Export Menu */}
        <Menu
          anchorEl={notificationsExportMenuAnchor}
          open={Boolean(notificationsExportMenuAnchor)}
          onClose={handleNotificationExportMenuClose}
        >
          <MenuItem onClick={copyNotificationsToClipboard}>
            <ListItemIcon>
              <FileCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportNotificationsToCSV}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportNotificationsToExcel}>
            <ListItemIcon>
              <TableViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excel</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportNotificationsToPDF}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PDF</ListItemText>
          </MenuItem>
        </Menu>
      </Paper>

      <InfoNotification
        NotificationData={selectedNotification}
        open={showInformationNotification}
        onClose={() => setShowInformationNotification(false)}
      />
    </Grid>
  );
}
